import {
  BufferAttribute,
  BufferGeometry,
  Color,
  DoubleSide,
  Group,
  Matrix4,
  Mesh,
  MeshStandardMaterial,
  type Object3D,
} from 'three';
import { EventEmitter } from '../core/EventEmitter';
import { disposeObject } from '../core/SceneResourceDisposer';
import { IfcLoaderService } from '../ifc/IfcLoaderService';
import type { ParsedGeometry, ViewerModel } from './types';

export interface ModelManagerState {
  models: readonly ViewerModel[];
  busyCount: number;
}

function colorKey(color: readonly number[]): string {
  return color.map((component) => component.toFixed(3)).join(':');
}

export class ModelManager {
  readonly changed = new EventEmitter<ModelManagerState>();
  private readonly models = new Map<string, ViewerModel>();
  private readonly abortControllers = new Map<string, AbortController>();
  private readonly loader = new IfcLoaderService();

  constructor(private readonly sceneRoot: Group) {}

  get state(): ModelManagerState {
    const models = [...this.models.values()];
    return {
      models,
      busyCount: models.filter((model) => model.status === 'loading').length,
    };
  }

  async addFiles(files: Iterable<File>): Promise<void> {
    const ifcFiles = [...files].filter(
      (file) => file.name.toLowerCase().endsWith('.ifc') || file.type === 'application/x-step',
    );
    await Promise.allSettled(ifcFiles.map((file) => this.addFile(file)));
  }

  setVisibility(id: string, visible: boolean): void {
    const model = this.models.get(id);
    if (!model) return;
    model.visible = visible;
    model.group.visible = visible;
    this.emit();
  }

  remove(id: string): void {
    const model = this.models.get(id);
    if (!model) return;
    this.abortControllers.get(id)?.abort();
    this.abortControllers.delete(id);
    this.sceneRoot.remove(model.group);
    disposeObject(model.group);
    this.models.delete(id);
    this.emit();
  }

  private async addFile(file: File): Promise<void> {
    const id = crypto.randomUUID();
    const group = new Group();
    group.name = file.name;
    const model: ViewerModel = {
      id,
      name: file.name,
      byteSize: file.size,
      group,
      visible: true,
      status: 'loading',
      geometryCount: 0,
    };
    const abortController = new AbortController();
    const materials = new Map<string, MeshStandardMaterial>();

    this.models.set(id, model);
    this.abortControllers.set(id, abortController);
    this.sceneRoot.add(group);
    this.emit();

    try {
      model.geometryCount = await this.loader.load(
        file,
        { onGeometry: (geometry) => this.addGeometry(group, materials, geometry) },
        abortController.signal,
      );
      if (!this.models.has(id)) return;
      model.status = 'ready';
    } catch (error) {
      if (!this.models.has(id) || abortController.signal.aborted) return;
      model.status = 'error';
      model.error = error instanceof Error ? error.message : 'Unable to parse IFC file.';
    } finally {
      this.abortControllers.delete(id);
      if (this.models.has(id)) this.emit();
    }
  }

  private addGeometry(
    group: Group,
    materials: Map<string, MeshStandardMaterial>,
    parsed: ParsedGeometry,
  ): void {
    const geometry = new BufferGeometry();
    const vertexCount = parsed.vertices.length / 6;
    const positions = new Float32Array(vertexCount * 3);
    const normals = new Float32Array(vertexCount * 3);
    for (let source = 0, target = 0; source < parsed.vertices.length; source += 6, target += 3) {
      positions[target] = parsed.vertices[source] ?? 0;
      positions[target + 1] = parsed.vertices[source + 1] ?? 0;
      positions[target + 2] = parsed.vertices[source + 2] ?? 0;
      normals[target] = parsed.vertices[source + 3] ?? 0;
      normals[target + 1] = parsed.vertices[source + 4] ?? 0;
      normals[target + 2] = parsed.vertices[source + 5] ?? 0;
    }
    geometry.setAttribute('position', new BufferAttribute(positions, 3));
    geometry.setAttribute('normal', new BufferAttribute(normals, 3));
    geometry.setIndex(new BufferAttribute(parsed.indices, 1));
    geometry.computeBoundingSphere();

    const key = colorKey(parsed.color);
    let material = materials.get(key);
    if (!material) {
      material = new MeshStandardMaterial({
        color: new Color(parsed.color[0], parsed.color[1], parsed.color[2]),
        opacity: parsed.color[3],
        transparent: parsed.color[3] < 0.999,
        depthWrite: parsed.color[3] >= 0.999,
        roughness: 0.78,
        metalness: 0.03,
        side: DoubleSide,
      });
      materials.set(key, material);
    }

    const mesh = new Mesh(geometry, material);
    mesh.applyMatrix4(new Matrix4().fromArray(parsed.matrix));
    group.add(mesh as Object3D);
  }

  private emit(): void {
    this.changed.emit(this.state);
  }
}

